/**
 * Base Accordion Module
 * Provides core accordion functionality with focus management
 * Can be extended by specific accordion implementations
 */

import { onDOMReady, devLog } from './utils.js';

export class BaseAccordion {
    constructor(options = {}) {
        this.selector = options.selector || '[data-accordion-toggle]';
        this.accordions = document.querySelectorAll(this.selector);
        this.debug = options.debug || false;
        
        if (this.debug) {
            devLog('BaseAccordion: Found', this.accordions.length, 'accordion toggles');
        }
        
        this.init();
    }

    init() {
        if (this.accordions.length === 0) {
            if (this.debug) {
                devLog('BaseAccordion: No accordion toggles found');
            }
            return;
        }
        
        this.accordions.forEach(toggle => {
            toggle.addEventListener('click', this.handleToggle.bind(this));
        });
        
        // Close all accordions on page load and disable focusable elements
        this.closeAll();
        
        // Disable focusable elements in all accordion content on initial load
        this.accordions.forEach(toggle => {
            const content = this.getContent(toggle);
            if (content) {
                this.disableFocusableElements(content);
            }
        });
        
        // Add focus tracking for debugging
        if (this.debug) {
            document.addEventListener('focusin', (event) => {
                console.log('[DEBUG] BaseAccordion: Focus moved to:', event.target.tagName, event.target.textContent?.trim().substring(0, 30), 'tabindex:', event.target.getAttribute('tabindex'));
            });
        }
    }

    getContent(toggle) {
        const contentId = toggle.getAttribute('aria-controls');
        return contentId ? document.getElementById(contentId) : null;
    }

    handleToggle(event) {
        event.preventDefault();
        
        const toggle = event.currentTarget;
        const content = this.getContent(toggle);
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        
        if (this.debug) {
            console.log('[DEBUG] BaseAccordion: Toggle clicked, isExpanded:', isExpanded);
        }
        
        if (isExpanded) {
            this.closeAccordion(toggle, content);
        } else {
            // Allow multiple accordions to stay open independently
            if (this.debug) {
                console.log('[DEBUG] BaseAccordion: Opening accordion (independent mode)');
            }
            this.openAccordion(toggle, content);
        }
    }

    openAccordion(toggle, content) {
        if (this.debug) {
            console.log('[DEBUG] BaseAccordion: Opening accordion');
        }
        
        toggle.setAttribute('aria-expanded', 'true');
        content.setAttribute('aria-hidden', 'false');
        
        // Add opening class for CSS transitions
        toggle.classList.add('accordion__toggle--expanded');
        content.classList.add('accordion__content--expanded');
        
        // Set max-height for smooth animation (use inner content height to avoid clipping on mobile)
        const inner = content.firstElementChild || content;
        // Temporarily set maxHeight to none to measure full height accurately if needed
        const prevMax = content.style.maxHeight;
        content.style.maxHeight = 'none';
        const targetHeight = inner.scrollHeight;
        // Apply the measured height for transition
        content.style.maxHeight = targetHeight + 'px';
        
        // Enable focusable elements inside the content
        this.enableFocusableElements(content);
    }

    closeAccordion(toggle, content) {
        if (this.debug) {
            console.log('[DEBUG] BaseAccordion: Closing accordion');
        }
        
        toggle.setAttribute('aria-expanded', 'false');
        content.setAttribute('aria-hidden', 'true');
        
        // Remove expanded classes
        toggle.classList.remove('accordion__toggle--expanded');
        content.classList.remove('accordion__content--expanded');
        
        // Collapse content
        content.style.maxHeight = '0px';
        
        // Disable focusable elements inside the content
        this.disableFocusableElements(content);
    }

    disableFocusableElements(content) {
        // Find all focusable elements inside the content
        const focusableElements = content.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        
        if (this.debug) {
            console.log('[DEBUG] BaseAccordion: Disabling', focusableElements.length, 'focusable elements');
        }
        
        focusableElements.forEach(element => {
            // Store the original tabindex if it exists
            if (element.hasAttribute('tabindex')) {
                element.setAttribute('data-original-tabindex', element.getAttribute('tabindex'));
            }
            // Set tabindex to -1 to remove from tab order
            element.setAttribute('tabindex', '-1');
            
            if (this.debug) {
                console.log('[DEBUG] BaseAccordion: Disabled focus for:', element.tagName, element.textContent?.trim().substring(0, 30));
            }
        });
    }

    enableFocusableElements(content) {
        // Find all focusable elements inside the content
        const focusableElements = content.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]'
        );
        
        if (this.debug) {
            console.log('[DEBUG] BaseAccordion: Enabling', focusableElements.length, 'focusable elements');
        }
        
        focusableElements.forEach(element => {
            // Restore original tabindex if it was stored and is not -1
            if (element.hasAttribute('data-original-tabindex')) {
                const originalTabindex = element.getAttribute('data-original-tabindex');
                if (originalTabindex === '-1') {
                    // If it was originally -1, set it to 0 to make it focusable
                    element.setAttribute('tabindex', '0');
                    if (this.debug) {
                        console.log('[DEBUG] BaseAccordion: Set tabindex to 0 for:', element.tagName, element.textContent?.trim().substring(0, 30));
                    }
                } else {
                    // Restore the original tabindex
                    element.setAttribute('tabindex', originalTabindex);
                    if (this.debug) {
                        console.log('[DEBUG] BaseAccordion: Restored tabindex for:', element.tagName, element.textContent?.trim().substring(0, 30), 'tabindex:', element.getAttribute('tabindex'));
                    }
                }
                element.removeAttribute('data-original-tabindex');
            } else {
                // If no original tabindex was stored, set to 0 to make it focusable
                element.setAttribute('tabindex', '0');
                if (this.debug) {
                    console.log('[DEBUG] BaseAccordion: Set tabindex to 0 for:', element.tagName, element.textContent?.trim().substring(0, 30));
                }
            }
        });
        
        // After enabling, let's check what the tab order looks like
        if (this.debug) {
            setTimeout(() => {
                console.log('[DEBUG] BaseAccordion: Checking tab order after enabling...');
                const allFocusable = document.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
                console.log('[DEBUG] BaseAccordion: Total focusable elements on page:', allFocusable.length);
                
                // Find the current accordion's focusable elements
                const accordionFocusable = content.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
                console.log('[DEBUG] BaseAccordion: Focusable elements in this accordion:', accordionFocusable.length);
                accordionFocusable.forEach((el, index) => {
                    console.log('[DEBUG] BaseAccordion: Accordion focusable', index, ':', el.tagName, el.textContent?.trim().substring(0, 30), 'tabindex:', el.getAttribute('tabindex'));
                });
            }, 100);
        }
    }

    closeAll(excludeToggle = null) {
        this.accordions.forEach(toggle => {
            // Skip the accordion that should remain open
            if (excludeToggle && toggle === excludeToggle) {
                return;
            }
            
            const content = this.getContent(toggle);
            if (content) {
                this.closeAccordion(toggle, content);
            }
        });
    }
} 